/**
 * The glyphs the slot badge draws, one per slot type.
 *
 * Ionicons supplies the mark wherever one exists. The text-lines rule and the
 * select mark have no Ionicons equivalent, so they are drawn here as tiny
 * 24px-viewBox strokes styled identically to the icon glyphs. Every glyph takes
 * its color from the badge around it (`--slot-accent`, inherited), so none of
 * them names a type or a color.
 *
 * `SlotViews` is what pairs a glyph with a type; this file only draws them.
 */

import React from 'react';
import { IonIcon } from '@ionic/react';
import { checkboxOutline, createOutline, flagOutline } from 'ionicons/icons';
import styles from './slots.module.css';

/** What every glyph is: a mark, drawn from nothing but the accent it inherits. */
export type SlotGlyph = () => React.ReactElement;

/** An Ionicons mark, sized for the badge. */
function iconGlyph(icon: string): SlotGlyph {
  return () => <IonIcon icon={icon} className={styles.badgeIcon} />;
}

export const TextGlyph: SlotGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 6h16M4 12h16M4 18h9" />
  </svg>
);

export const SelectGlyph: SlotGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="3" />
    <path d="M14 7h6M14 17h6" />
    <circle cx="7" cy="17" r="3" />
  </svg>
);

export const GoalGlyph = iconGlyph(flagOutline);
export const FillInGlyph = iconGlyph(createOutline);
export const OptionalGlyph = iconGlyph(checkboxOutline);
